/**
 * Нормализация progress-чанков (stage + message) в шаги таймлайна процесса.
 *
 * Таймлайн показывает ВЕСЬ ход работы агента: запуск → план источников →
 * сбор по каждому источнику (с деталью кол-ва строк/фрагментов) → синтез →
 * шаги построения ответа. Источник правды по виду шага — `resolveProgressMeta`
 * (tag/title по stage), деталь завершения tool'а приходит в `tool.end`.
 *
 * Контракт шага: { id, kind:'progress', stage, tool, status, tag, title,
 *                  message, detail }. Тот же контракт собирает бэкенд (PHP
 *                  ChatResponseVariantService.extractProgressSteps) для
 *                  персиста, чтобы процесс был виден после reload/в share.
 */

// Этапы-вехи, попадающие в таймлайн. tool.start включаем для любого
// инструмента (показывает реальную работу). tool.end — это деталь завершения
// предыдущего tool-шага, отдельным шагом не становится.
//
// llm.iteration — фаза «думания» модели между ходами агента. Это самые долгие
// паузы (модель решает, какие tools звать / пишет ответ), поэтому показываем их
// отдельным активным шагом с живым таймером — иначе таймлайн выглядит зависшим,
// а tool-шаги вываливаются пачкой только после ответа LLM.
const MILESTONE_STAGES = new Set(['start', 'plan', 'synthesis', 'llm.iteration'])

export function isTimelineStage(stage = '') {
    const s = String(stage)
    return MILESTONE_STAGES.has(s) || s === 'tool.start'
}

export function resolveProgressMeta(stage = '') {
    const s = String(stage)

    if (s === 'start') {
        return { tag: 'СТАРТ', title: 'Запуск агента' }
    }
    if (s === 'plan') {
        return { tag: 'ПЛАН', title: 'Планирую источники' }
    }
    if (s === 'synthesis') {
        return { tag: 'СИНТЕЗ', title: 'Сверяю и объединяю источники' }
    }
    if (s.startsWith('llm')) {
        return { tag: 'АНАЛИЗ', title: 'Анализ запроса' }
    }
    if (s.includes('query_clickhouse')) {
        return { tag: 'ДАННЫЕ', title: 'Поиск в базе регистраций' }
    }
    if (s.includes('web_search') || s.includes('yandex')) {
        return { tag: 'ИНТЕРНЕТ', title: 'Поиск в интернете' }
    }
    if (s.includes('rag')) {
        return { tag: 'БАЗА ЗНАНИЙ', title: 'Поиск в базе знаний' }
    }
    if (s.includes('make_card_compare') || s.includes('make_card_market') || s.includes('make_chart')) {
        return { tag: 'ГРАФИК', title: 'Построение графика' }
    }
    if (s.includes('make_card_brands_table')) {
        return { tag: 'ТАБЛИЦА', title: 'Агрегация по брендам' }
    }
    if (s.includes('make_card_regions')) {
        return { tag: 'РЕГИОНЫ', title: 'Разбивка по регионам' }
    }
    if (s.includes('make_card_forecast')) {
        return { tag: 'ПРОГНОЗ', title: 'Построение сценариев' }
    }
    if (s.includes('make_list')) {
        return { tag: 'СПИСОК', title: 'Формирование списка' }
    }
    if (s.includes('make_callout')) {
        return { tag: 'ВЫВОД', title: 'Подготовка выводов' }
    }
    if (s.includes('add_followups')) {
        return { tag: 'ШАГИ', title: 'Следующие вопросы' }
    }
    if (s.includes('finalize_response')) {
        return { tag: 'ФИНАЛ', title: 'Сборка ответа' }
    }
    if (s.startsWith('text')) {
        return { tag: 'ТЕКСТ', title: 'Формирование ответа' }
    }
    // tool.start без распознанного инструмента — нейтральная подпись.
    if (s === 'tool.start') {
        return { tag: 'ШАГ', title: 'Работаю с источником' }
    }

    return { tag: 'ЭТАП', title: 'Обработка' }
}

/**
 * Применить progress-чанк к таймлайну сообщения.
 *
 * - tool.end → завершает соответствующий активный tool-шаг и пишет деталь
 *   (кол-во строк/фрагментов/результатов);
 * - tool.start → новый активный шаг (НЕ закрывает другие активные tool-шаги —
 *   источники могут собираться параллельно);
 * - прочие вехи (start/plan/synthesis) → новый активный шаг, закрывают все
 *   предыдущие активные;
 * - не-таймлайновые stage игнорируются (но `message.progress` обновляется
 *   вызывающим как однострочный статус).
 */
export function pushProgressStep(message, chunk) {
    const stage = String(chunk?.stage || '')
    const messageText = chunk?.message || ''
    const tool = String(chunk?.tool || '')
    message.progress = messageText

    if (!Array.isArray(message.progressSteps)) {
        message.progressSteps = []
    }
    const steps = message.progressSteps

    // Деталь завершения инструмента — на активный шаг того же tool'а.
    if (stage === 'tool.end' && tool) {
        for (let i = steps.length - 1; i >= 0; i -= 1) {
            if (steps[i].tool === tool) {
                closeStep(steps[i])
                if (messageText && messageText !== 'думаю над ответом') {
                    steps[i].detail = messageText
                }
                break
            }
        }
        return
    }

    if (!isTimelineStage(stage)) {
        return
    }

    const last = steps[steps.length - 1]
    if (last && last.message === messageText && last.stage === stage) {
        return
    }

    // Новый tool.start не закрывает другие активные tool.start (параллельный
    // сбор). Любая иная веха закрывает все активные шаги.
    steps.forEach(step => {
        if (step.status === 'active' && !(stage === 'tool.start' && step.stage === 'tool.start')) {
            closeStep(step)
        }
    })

    // Для tool-шагов смысл несёт имя инструмента (stage всегда 'tool.start').
    const meta = resolveProgressMeta(stage === 'tool.start' && tool ? tool : stage)
    steps.push({
        id: `step-${steps.length}-${stage || 'progress'}`,
        kind: 'progress',
        stage,
        tool,
        status: 'active',
        ...meta,
        message: messageText,
        detail: '',
        // Тайминг шага: момент старта фиксируем по приходу чанка, длительность
        // считаем при закрытии (closeStep). Для шагов, восстановленных из БД,
        // durationMs приходит с бэкенда (см. ChatResponseVariantService).
        startedAt: Date.now(),
        durationMs: null,
    })
}

// Закрыть шаг: пометить done и зафиксировать длительность (если ещё не задана
// и известно время старта).
function closeStep(step) {
    step.status = 'done'
    if ((step.durationMs === null || step.durationMs === undefined) && step.startedAt) {
        step.durationMs = Date.now() - step.startedAt
    }
}

export function finishProgressSteps(message) {
    if (!Array.isArray(message.progressSteps)) return
    message.progressSteps.forEach(step => {
        closeStep(step)
    })
}
